import React, { useState, useEffect } from 'react';
import { toast } from '../Toast';
import { askConfirm } from '../ui';
import { crmApi } from '../../services/crmApi';
import { Users, Plus, Trash2, Save, X, Edit, ChevronDown, ChevronUp, UserCheck, Clock, XCircle } from 'lucide-react';

interface Guarantor {
  id: number;
  application_id: number;
  first_name: string;
  last_name: string;
  pesel: string | null;
  phone: string | null;
  email: string | null;
  employer: string | null;
  monthly_income: number | null;
  employment_type: string;
  employment_since: string | null;
  address: string | null;
  status: string;
  notes: string | null;
}

const EMPLOYMENT_TYPES: Record<string, string> = {
  umowa_o_prace: 'Umowa o pracę',
  umowa_zlecenie: 'Umowa zlecenie',
  dzialalnosc: 'Działalność gosp.',
  emerytura: 'Emerytura',
  renta: 'Renta',
  inne: 'Inne',
};

const STATUS_ICONS: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  pending:  { icon: <Clock size={12} />,     color: 'bg-amber-100 text-amber-700',  label: 'Oczekuje' },
  verified: { icon: <UserCheck size={12} />,  color: 'bg-green-100 text-green-700',  label: 'Zweryfikowany' },
  rejected: { icon: <XCircle size={12} />,    color: 'bg-red-100 text-red-700',      label: 'Odrzucony' },
};

const emptyForm = {
  firstName: '', lastName: '', pesel: '', phone: '', email: '',
  employer: '', monthlyIncome: '', employmentType: 'umowa_o_prace',
  employmentSince: '', address: '', notes: '',
};

export const GuarantorsSection: React.FC<{ applicationId: number }> = ({ applicationId }) => {
  const [guarantors, setGuarantors] = useState<Guarantor[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const load = async () => {
    try {
      const res = await crmApi.getGuarantors(applicationId);
      setGuarantors(res.guarantors || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [applicationId]);

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await crmApi.updateGuarantor(editingId, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          pesel: form.pesel || null,
          phone: form.phone || null,
          email: form.email || null,
          employer: form.employer || null,
          monthlyIncome: form.monthlyIncome ? Number(form.monthlyIncome) : null,
          employmentType: form.employmentType,
          employmentSince: form.employmentSince || null,
          address: form.address || null,
          notes: form.notes || null,
        });
      } else {
        await crmApi.createGuarantor(applicationId, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          pesel: form.pesel || null,
          phone: form.phone || null,
          email: form.email || null,
          employer: form.employer || null,
          monthlyIncome: form.monthlyIncome ? Number(form.monthlyIncome) : null,
          employmentType: form.employmentType,
          employmentSince: form.employmentSince || null,
          address: form.address || null,
          notes: form.notes || null,
        });
      }
      setForm(emptyForm);
      setShowForm(false);
      setEditingId(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Błąd zapisu poręczyciela');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (g: Guarantor) => {
    setForm({
      firstName: g.first_name,
      lastName: g.last_name,
      pesel: g.pesel || '',
      phone: g.phone || '',
      email: g.email || '',
      employer: g.employer || '',
      monthlyIncome: g.monthly_income?.toString() || '',
      employmentType: g.employment_type || 'umowa_o_prace',
      employmentSince: g.employment_since ? g.employment_since.substring(0, 10) : '',
      address: g.address || '',
      notes: g.notes || '',
    });
    setEditingId(g.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!(await askConfirm({ title: 'Usunąć poręczyciela?' }))) return;
    try {
      await crmApi.deleteGuarantor(id);
      load();
    } catch { toast.error('Błąd usuwania'); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await crmApi.updateGuarantor(id, { status });
      load();
    } catch { toast.error('Błąd zmiany statusu'); }
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const statusInfo = (s: string) => STATUS_ICONS[s] || STATUS_ICONS.pending;

  return (
    <div className="bg-gradient-to-br from-violet-50 to-white rounded-lg shadow border border-violet-200">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-violet-50/50 transition-colors"
      >
        <h3 className="font-bold text-violet-800 flex items-center gap-2">
          <Users size={18} />
          Poręczyciele
          {guarantors.length > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-violet-200 text-violet-700">{guarantors.length}</span>
          )}
        </h3>
        {expanded ? <ChevronUp size={16} className="text-violet-400" /> : <ChevronDown size={16} className="text-violet-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-400 animate-pulse">Ładowanie...</p>
          ) : (
            <>
              {/* Guarantor cards */}
              {guarantors.map(g => {
                const si = statusInfo(g.status);
                return (
                  <div key={g.id} className="bg-white rounded-lg border p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{g.first_name} {g.last_name}</span>
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${si.color}`}>
                          {si.icon} {si.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => handleEdit(g)} className="p-1 text-gray-400 hover:text-blue-600" title="Edytuj">
                          <Edit size={14} />
                        </button>
                        <button type="button" onClick={() => handleDelete(g.id)} className="p-1 text-gray-400 hover:text-red-600" title="Usuń">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                      {g.pesel && <div>PESEL: <span className="font-mono text-gray-800">{g.pesel}</span></div>}
                      {g.phone && <div>Tel: <span className="text-gray-800">{g.phone}</span></div>}
                      {g.email && <div className="col-span-2">Email: <span className="text-gray-800">{g.email}</span></div>}
                      {g.employer && <div>Pracodawca: <span className="text-gray-800">{g.employer}</span></div>}
                      {g.monthly_income && <div>Dochód: <span className="font-semibold text-emerald-700">{Number(g.monthly_income).toLocaleString('pl-PL')} zł</span></div>}
                      {g.employment_type && <div>Zatrudnienie: <span className="text-gray-800">{EMPLOYMENT_TYPES[g.employment_type] || g.employment_type}</span></div>}
                      {g.employment_since && <div>Od: <span className="text-gray-800">{g.employment_since.substring(0, 10)}</span></div>}
                    </div>
                    {/* Quick status change */}
                    <div className="flex gap-1 pt-1">
                      {['pending', 'verified', 'rejected'].map(s => {
                        const info = STATUS_ICONS[s];
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => handleStatusChange(g.id, s)}
                            className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${g.status === s ? info.color + ' font-bold' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`}
                          >
                            {info.label}
                          </button>
                        );
                      })}
                    </div>
                    {g.notes && <p className="text-xs text-gray-500 italic border-t pt-1">{g.notes}</p>}
                  </div>
                );
              })}

              {guarantors.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 text-center py-2">Brak poręczycieli</p>
              )}

              {/* Add/Edit form */}
              {showForm && (
                <div className="bg-violet-50 rounded-lg border border-violet-200 p-3 space-y-2">
                  <h4 className="text-xs font-semibold text-violet-700 uppercase">
                    {editingId ? 'Edycja poręczyciela' : 'Nowy poręczyciel'}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" placeholder="Imię *" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="text-sm p-2 border rounded" />
                    <input type="text" placeholder="Nazwisko *" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="text-sm p-2 border rounded" />
                    <input type="text" placeholder="PESEL" maxLength={11} value={form.pesel} onChange={e => setForm(f => ({ ...f, pesel: e.target.value.replace(/\D/g, '') }))} className="text-sm p-2 border rounded font-mono" />
                    <input type="tel" placeholder="Telefon" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="text-sm p-2 border rounded" />
                    <input type="email" placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="text-sm p-2 border rounded col-span-2" />
                    <input type="text" placeholder="Pracodawca" value={form.employer} onChange={e => setForm(f => ({ ...f, employer: e.target.value }))} className="text-sm p-2 border rounded" />
                    <input type="number" placeholder="Dochód msc (zł)" min="0" value={form.monthlyIncome} onChange={e => setForm(f => ({ ...f, monthlyIncome: e.target.value }))} className="text-sm p-2 border rounded" />
                    <select value={form.employmentType} onChange={e => setForm(f => ({ ...f, employmentType: e.target.value }))} className="text-sm p-2 border rounded" aria-label="Typ zatrudnienia">
                      {Object.entries(EMPLOYMENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <input type="date" placeholder="Zatrudniony od" value={form.employmentSince} onChange={e => setForm(f => ({ ...f, employmentSince: e.target.value }))} className="text-sm p-2 border rounded" />
                  </div>
                  <input type="text" placeholder="Adres zamieszkania" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="w-full text-sm p-2 border rounded" />
                  <textarea placeholder="Notatki" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full text-sm p-2 border rounded resize-none" />
                  <div className="flex gap-2">
                    <button type="button" onClick={handleSave} disabled={saving || !form.firstName.trim() || !form.lastName.trim()} className="flex items-center gap-1 px-3 py-1.5 bg-violet-600 text-white rounded text-sm font-medium hover:bg-violet-700 disabled:opacity-50">
                      <Save size={14} /> {saving ? 'Zapisuję...' : editingId ? 'Zapisz zmiany' : 'Dodaj poręczyciela'}
                    </button>
                    <button type="button" onClick={cancelForm} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200">
                      <X size={14} /> Anuluj
                    </button>
                  </div>
                </div>
              )}

              {/* Add button */}
              {!showForm && (
                <button
                  type="button"
                  onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true); }}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-lg border border-dashed border-violet-300 transition-colors"
                >
                  <Plus size={16} /> Dodaj poręczyciela
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
